import { AfterViewInit, ChangeDetectionStrategy, Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { BehaviorSubject, combineLatest, from, Observable, switchMap, take, tap } from 'rxjs';
import { GlobalMetaDataEntityService } from '../services/global-metadata.entity.service';
import { GlobalMetaDataEntity } from '../global-metadata.entity';
import { FormArray, FormGroup } from '@angular/forms';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { EntityStateModel } from '../../core/state/entity.state';
import { makeQueryKey } from '../../utils/entity-states/query-key';
import { Store } from '@ngxs/store';
import { CommonModule } from '@angular/common';
import { GlobalMetadataFactoryService } from '../services/global-metadata.factory.service';
import { map } from 'rxjs/operators';

@Component({
  selector: 'pixl-global-metadata-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatSortModule,
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    MatPaginator,
    MatCheckbox
  ],
  templateUrl: './global-metadata-select.component.html',
  styleUrl: './global-metadata-select.component.scss'
})

export class GlobalMetadataSelectComponent implements OnInit, AfterViewInit {
  private store = inject(Store);
  private globalMetadataFactory = inject(GlobalMetadataFactoryService);
  private refresh$ = new BehaviorSubject<void>(undefined);

  @Input() targetArrayName: 'consumerMetadata' | 'metadata' = 'consumerMetadata';
  @Input() parentForm!: FormGroup;
  @Input() consumerMetadataIds: string[] = [];

  private globalMetaDataService = inject(GlobalMetaDataEntityService);

  globalMetaData$!: Observable<GlobalMetaDataEntity[]>;

  selectedIds = new Set<string>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  pageSize = 100;
  pageIndex = 0;
  totalItems = 1000;

  private pageChange$ = new BehaviorSubject<{ pageIndex: number, pageSize: number }>({ pageIndex: this.pageIndex, pageSize: this.pageSize });

  displayedColumns = ['select', 'id', 'name', 'description', 'fieldConfigName', 'fieldConfigDatatype'];

  ngOnInit() {
    this.selectedIds = new Set(this.consumerMetadataIds || []);

    this.globalMetaData$ = combineLatest([
      this.pageChange$,
      this.refresh$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }]) => {
        const query: Record<string, string | number> = {
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': 'name'
        };

        return from(this.globalMetaDataService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.globalMetaDataService.allByQuery(query))),
          map(all => {
            const selected: GlobalMetaDataEntity[] = [];
            const others: GlobalMetaDataEntity[] = [];
            all.forEach(m => {
              if (this.selectedIds.has(m.id)) {
                selected.push(m);
              }
              else {
                others.push(m);
              }
            });
            return [...selected, ...others];
          }),
          tap(() => {
            const state = this.store.selectSnapshot(this.globalMetaDataService.stateClass) as EntityStateModel<GlobalMetaDataEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state.meta?.[queryKey]?.page?.total ?? 0;
          })
        );
      })
    );
  }

  ngAfterViewInit() {
    this.paginator.page.subscribe((event: PageEvent) => {
      this.pageChange$.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  isSelected(id: string): boolean {
    return this.selectedIds.has(id);
  }

  toggle(id: string, event: MatCheckboxChange) {
    if (event.checked) {
      this.selectedIds.add(id);
    }
    else {
      this.selectedIds.delete(id);
    }
    this.updateFormArray();
  }

  private updateFormArray() {
    if (!this.parentForm) return;

    const array = this.parentForm.get(this.targetArrayName) as FormArray;

    array.clear();

    this.globalMetaData$.pipe(take(1)).subscribe(all => {
      const selected = all.filter(m => this.selectedIds.has(m.id));

      selected.forEach(m => {
        array.push(
          this.globalMetadataFactory.createMetadataGroup(m)
        );
      });
    });
  }

  trackById(index: number, globalMetaData: GlobalMetaDataEntity): string {
    return globalMetaData.id;
  }
}
